// Time scrubber — visualization-layer time control over the IntelSignal feed.
//
// Phase C-1 keeps the scrubber client-side: it does not change the
// `useIntelFeed` filter (so we never re-fetch on a scrub), it just exposes
// `asOf` to consumers who clip the rendered set to `occurred_at <= asOf`.
// Phase D wires the same hook to a server-side as-of-replay endpoint.
//
// Range is fixed at 24h ending "now" — matches the design doc's bottom
// scrubber strip. The hook returns a stable `range24h` so the consumer
// can render the sparkline without re-deriving on every render.

import { useCallback, useMemo, useRef, useState } from 'react';

export interface UseTimeScrubberOptions {
  /** ISO ceiling. Defaults to current Date at hook mount, refreshed on
   *  setLive(). Pass a fixed value in tests. */
  now?: () => Date;
  /** Initial mode. Defaults to live (handle locked at right edge). */
  initialLive?: boolean;
}

export interface UseTimeScrubberResult {
  /** Current scrub position. `null` means "live" — clip to <= now. */
  asOf: Date | null;
  /** True iff the handle is locked at the right edge. */
  isLive: boolean;
  /** Move the handle. Pass `null` to lock to live. */
  setAsOf: (next: Date | null) => void;
  /** Snap the handle back to the right edge. Equivalent to setAsOf(null). */
  setLive: () => void;
  /** 24h range ending at "now" — recomputed when setLive() is called. */
  range24h: { from: Date; to: Date };
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function useTimeScrubber(opts: UseTimeScrubberOptions = {}): UseTimeScrubberResult {
  const nowFn = opts.now ?? (() => new Date());
  const nowRef = useRef<Date>(nowFn());
  const [asOf, setAsOfState] = useState<Date | null>(
    opts.initialLive === false ? nowRef.current : null,
  );
  const [, force] = useState(0);

  const setAsOf = useCallback((next: Date | null) => {
    setAsOfState(next);
  }, []);

  const setLive = useCallback(() => {
    nowRef.current = nowFn();
    setAsOfState(null);
    force((n) => n + 1);
  }, [nowFn]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: nowRef.current is mutated explicitly by setLive() which also bumps the force counter — that's the dep we re-read on.
  const range24h = useMemo(() => {
    const to = nowRef.current;
    return { from: new Date(to.getTime() - DAY_MS), to };
  }, [nowRef.current]);

  return {
    asOf,
    isLive: asOf === null,
    setAsOf,
    setLive,
    range24h,
  };
}
